import React from 'react';
import { Mail, Phone, Stethoscope, UserRound } from 'lucide-react';

/** Preferred campus doctor card for referral review surfaces. */
export default function ReferralDoctorCard({ doctor, fallbackName }) {
  const name = doctor?.name || fallbackName;
  if (!name) {
    return (
      <div
        className="rounded-2xl border border-dashed border-amber-300 bg-amber-50/80 px-4 py-4"
        data-testid="referral-doctor-missing"
      >
        <p className="text-sm font-semibold text-amber-950">Campus doctor not assigned</p>
        <p className="mt-1 text-xs text-amber-800/90">
          This request has no preferred medical officer. Ask reception to re-submit with an on-duty doctor.
        </p>
      </div>
    );
  }

  const role = String(doctor?.clinic_role || '').replace(/_/g, ' ');
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#D5DEE8] bg-white shadow-sm"
      data-testid="referral-doctor-card"
    >
      <div
        className="absolute inset-x-0 top-0 h-1.5"
        style={{ background: 'linear-gradient(90deg, #071B34, #F15A22, #C4A35A)' }}
      />
      <div className="flex gap-4 p-4 pt-5">
        <div
          className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl text-lg font-bold text-white shadow-md"
          style={{ background: 'linear-gradient(145deg, #0C2744, #071B34)' }}
        >
          {initials || <UserRound className="h-6 w-6" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#C4A35A]">
            Campus doctor · letter issuer
          </p>
          <h4 className="mt-0.5 font-heading text-lg font-semibold text-[#071B34] truncate">
            {name.startsWith('Dr') ? name : `Dr ${name}`}
          </h4>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#5A6B7C]">
            {(doctor?.specialization || doctor?.department) && (
              <span className="inline-flex items-center gap-1">
                <Stethoscope className="h-3.5 w-3.5 text-[#F15A22]" />
                {doctor.specialization || doctor.department}
              </span>
            )}
            {role ? <span className="capitalize">{role}</span> : null}
          </p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#5A6B7C]">
            {doctor?.email ? (
              <span className="inline-flex items-center gap-1 truncate">
                <Mail className="h-3.5 w-3.5" /> {doctor.email}
              </span>
            ) : null}
            {doctor?.phone ? (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3.5 w-3.5" /> {doctor.phone}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
